import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import {
    BillingApi,
    DocumentApi,
    EBUtilitiesApi,
    EnergyCommunityApi,
    MemberContractApi,
    EnergyDataApi,
    GridApi,
    GridOperatorApi,
    IdentityApi,
    InvoiceApi,
    MeteringPointApi,
    MeteringPointCostCenterApi,
    MeteringPointContractApi,
    OnboardingControllingApi,
    UserNotificationApi,
    LegalPersonApi,
    PaymentApi,
    PaymentInfoApi,
    ProcessApi,
    SepaMandateApi,
    BillingBatchApi,
    SystemBannerApi,
    TariffAssignmentApi,
    TariffCalculationApi,
    TariffComponentApi,
    TariffComponentValueApi,
    TariffPackageApi,
    UserApi,
    VersionApi,
} from "./api";
import { PUBLIC_ROUTES } from "@/LocalRoutes";
import {
    getRetryDelayMs,
    isIdempotentMethod,
    shouldRetryRequest,
} from "@/lib/request-retry";
import {
    enterPlatformUnavailable,
    isMaintenanceResponse,
    isErrorPageResponse,
    isPlatformReachable,
    waitForPlatform,
} from "@/lib/platform-unavailable";

export const API_V_LATEST = "1.0";

export const AxiosClient = axios.create({
    baseURL: window.location.origin,
});

AxiosClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const config = error.config as
            | (InternalAxiosRequestConfig & {
                  retryCount?: number;
                  waitedForPlatform?: boolean;
              })
            | undefined;
        if (
            isMaintenanceResponse(
                error.response?.status,
                error.response?.headers,
            )
        ) {
            enterPlatformUnavailable("maintenance");
            return Promise.reject(
                Object.assign(error, { suppressToast: true }),
            );
        }

        const attempt = config?.retryCount ?? 0;
        if (
            config &&
            !config.signal?.aborted &&
            shouldRetryRequest(error.response?.status, config.method, attempt)
        ) {
            config.retryCount = attempt + 1;
            const retryAfter: unknown =
                error.response?.headers?.["retry-after"];
            const delay = getRetryDelayMs(
                typeof retryAfter === "string" ? retryAfter : undefined,
                attempt,
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
            return AxiosClient.request(config);
        }

        // A deploy leaves the backend unreachable for longer than the retries
        // cover, so the outage screen only shows once waitForPlatform() gives
        // up. A successful first probe keeps a backend 503 (e.g. DB overload)
        // on the normal error path.
        if (
            config &&
            !config.waitedForPlatform &&
            isErrorPageResponse(
                error.response?.status,
                error.response?.headers,
            ) &&
            !(await isPlatformReachable())
        ) {
            config.waitedForPlatform = true;
            const recovered = waitForPlatform().then((reachable) => {
                if (!reachable) enterPlatformUnavailable("outage");
                return reachable;
            });
            // A mutation is not repeated and fails right away, so its form
            // stays mounted and the user can resubmit.
            if (!isIdempotentMethod(config.method)) {
                return Promise.reject(error);
            }
            if (await recovered) {
                return AxiosClient.request(config);
            }
            return Promise.reject(
                Object.assign(error, { suppressToast: true }),
            );
        }

        if (error.response?.status === 401) {
            const currentPath = window.location.pathname;
            const isPublicRoute = PUBLIC_ROUTES.includes(currentPath);

            if (!isPublicRoute) {
                window.location.replace("/login");
            }

            return Promise.reject(
                Object.assign(error, { suppressToast: true }),
            );
        }
        return Promise.reject(error);
    },
);

export const ApiClients = (() => {
    let version: VersionApi | null = null;
    let identity: IdentityApi | null = null;
    let user: UserApi | null = null;
    let legalPerson: LegalPersonApi | null = null;
    let energyCommunity: EnergyCommunityApi | null = null;
    let memberContract: MemberContractApi | null = null;
    let meteringPoint: MeteringPointApi | null = null;
    let meteringPointCostCenter: MeteringPointCostCenterApi | null = null;
    let meteringPointContract: MeteringPointContractApi | null = null;
    let payment: PaymentApi | null = null;
    let paymentInfo: PaymentInfoApi | null = null;
    let process: ProcessApi | null = null;
    let sepaMandate: SepaMandateApi | null = null;
    let ebUtilitiesApi: EBUtilitiesApi | null = null;
    let gridApi: GridApi | null = null;
    let gridOperatorApi: GridOperatorApi | null = null;
    let energyDataApi: EnergyDataApi | null = null;
    let systemBanner: SystemBannerApi | null = null;
    let tariffAssignmentApi: TariffAssignmentApi | null = null;
    let tariffCalculationApi: TariffCalculationApi | null = null;
    let tariffComponentApi: TariffComponentApi | null = null;
    let tariffComponentValueApi: TariffComponentValueApi | null = null;
    let tariffPackageApi: TariffPackageApi | null = null;
    let document: DocumentApi | null = null;
    let notifications: UserNotificationApi | null = null;
    let billing: BillingApi | null = null;
    let invoice: InvoiceApi | null = null;
    let onboardingControlling: OnboardingControllingApi | null = null;
    let billingBatch: BillingBatchApi | null = null;

    return {
        get Version() {
            if (!version) {
                version = new VersionApi(undefined, undefined, AxiosClient);
            }
            return version;
        },
        get Identity() {
            if (!identity) {
                identity = new IdentityApi(undefined, undefined, AxiosClient);
            }
            return identity;
        },
        get User() {
            if (!user) {
                user = new UserApi(undefined, undefined, AxiosClient);
            }
            return user;
        },
        get LegalPerson() {
            if (!legalPerson) {
                legalPerson = new LegalPersonApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return legalPerson;
        },
        get EnergyCommunity() {
            if (!energyCommunity) {
                energyCommunity = new EnergyCommunityApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return energyCommunity;
        },
        get MemberContract() {
            if (!memberContract) {
                memberContract = new MemberContractApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return memberContract;
        },
        get MeteringPoint() {
            if (!meteringPoint) {
                meteringPoint = new MeteringPointApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return meteringPoint;
        },
        get MeteringPointCostCenter() {
            if (!meteringPointCostCenter) {
                meteringPointCostCenter = new MeteringPointCostCenterApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return meteringPointCostCenter;
        },
        get MeteringPointContract() {
            if (!meteringPointContract) {
                meteringPointContract = new MeteringPointContractApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return meteringPointContract;
        },
        get Payment() {
            if (!payment) {
                payment = new PaymentApi(undefined, undefined, AxiosClient);
            }
            return payment;
        },
        get PaymentInfo() {
            if (!paymentInfo) {
                paymentInfo = new PaymentInfoApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return paymentInfo;
        },
        get SepaMandate() {
            if (!sepaMandate) {
                sepaMandate = new SepaMandateApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return sepaMandate;
        },
        get Process() {
            if (!process) {
                process = new ProcessApi(undefined, undefined, AxiosClient);
            }
            return process;
        },
        get EBUtilities() {
            if (!ebUtilitiesApi) {
                ebUtilitiesApi = new EBUtilitiesApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return ebUtilitiesApi;
        },
        get Grid() {
            if (!gridApi) {
                gridApi = new GridApi(undefined, undefined, AxiosClient);
            }
            return gridApi;
        },
        get GridOperator() {
            if (!gridOperatorApi) {
                gridOperatorApi = new GridOperatorApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return gridOperatorApi;
        },
        get EnergyData() {
            if (!energyDataApi) {
                energyDataApi = new EnergyDataApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return energyDataApi;
        },
        get SystemBanner() {
            if (!systemBanner) {
                systemBanner = new SystemBannerApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return systemBanner;
        },
        get TariffAssignment() {
            if (!tariffAssignmentApi) {
                tariffAssignmentApi = new TariffAssignmentApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return tariffAssignmentApi;
        },
        get TariffCalculation() {
            if (!tariffCalculationApi) {
                tariffCalculationApi = new TariffCalculationApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return tariffCalculationApi;
        },
        get Document() {
            if (!document) {
                document = new DocumentApi(undefined, undefined, AxiosClient);
            }
            return document;
        },
        get Notification() {
            if (!notifications) {
                notifications = new UserNotificationApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return notifications;
        },
        get TariffComponent() {
            if (!tariffComponentApi) {
                tariffComponentApi = new TariffComponentApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return tariffComponentApi;
        },
        get TariffComponentValue() {
            if (!tariffComponentValueApi) {
                tariffComponentValueApi = new TariffComponentValueApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return tariffComponentValueApi;
        },
        get TariffPackage() {
            if (!tariffPackageApi) {
                tariffPackageApi = new TariffPackageApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return tariffPackageApi;
        },
        get OnboardingControlling() {
            if (!onboardingControlling) {
                onboardingControlling = new OnboardingControllingApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return onboardingControlling;
        },
        get Billing() {
            if (!billing) {
                billing = new BillingApi(undefined, undefined, AxiosClient);
            }
            return billing;
        },
        get Invoice() {
            if (!invoice) {
                invoice = new InvoiceApi(undefined, undefined, AxiosClient);
            }
            return invoice;
        },
        get BillingBatch() {
            if (!billingBatch) {
                billingBatch = new BillingBatchApi(
                    undefined,
                    undefined,
                    AxiosClient,
                );
            }
            return billingBatch;
        },
    };
})();
